import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import EmptyState from '@/components/molecules/EmptyState';
import { DataTable } from '@/components/organisms/DataTable';

import UserAccessModal from './UserAccessModal';
import { buildUserTableColumns } from './userTableColumns';
import type { Location, TableUser } from './userTableHelpers';

const EMPTY_LOCATION_IDS: number[] = [];

interface UserTableProps {
    users: TableUser[];
    isFetching: boolean;
    canDeleteUsers: boolean;
    canImpersonateUsers: boolean;
    canManageAccess: boolean;
    clientId: number;
    clientLocations: Location[];
    deleteUser: (id: number) => Promise<unknown>;
    impersonateUser: (id: number) => Promise<unknown>;
    onAccessSaved: () => void;
    filter?: string;
}

function UserTable({
    isFetching,
    users,
    canDeleteUsers,
    canImpersonateUsers,
    canManageAccess,
    clientId,
    clientLocations,
    deleteUser,
    impersonateUser,
    onAccessSaved,
    filter = '',
}: UserTableProps) {
    const { t } = useTranslation();

    const [accessModalUser, setAccessModalUser] = useState<TableUser | null>(null);

    const clientLocationIds = useMemo(
        () => clientLocations.map((location) => location.id),
        [clientLocations]
    );

    const columns = useMemo(
        () =>
            buildUserTableColumns({
                t,
                clientId,
                clientLocationIds,
                canManageAccess,
                canImpersonateUsers,
                canDeleteUsers,
                impersonateUser,
                deleteUser,
                onManageAccess: setAccessModalUser,
            }),
        [
            t,
            clientId,
            clientLocationIds,
            canManageAccess,
            canImpersonateUsers,
            canDeleteUsers,
            impersonateUser,
            deleteUser,
        ]
    );

    const accessModalClientUser = accessModalUser?.clientUsers.find(
        (cu) => cu.clients.id === clientId
    );

    if (isFetching) {
        return <Spinner size="lg" />;
    }

    return (
        <>
            {users.length === 0 ? (
                <EmptyState title={t('common.labels.noData')} appearance="quiet" size="spacious" />
            ) : (
                <DataTable
                    responsive
                    data={users}
                    columns={columns}
                    filter={filter}
                    enablePagination={true}
                    pageSize={15}
                    pageSizeOptions={[15, 25, 50]}
                />
            )}

            {accessModalUser && (
                <UserAccessModal
                    isOpen={true}
                    onClose={() => setAccessModalUser(null)}
                    userId={accessModalUser.id}
                    userName={
                        `${accessModalUser.firstName || ''} ${accessModalUser.lastName || ''}`.trim() ||
                        accessModalUser.email
                    }
                    clientId={clientId}
                    clientLocations={clientLocations}
                    currentLocationIds={accessModalClientUser?.locationIds ?? EMPTY_LOCATION_IDS}
                    isFullClientAccess={accessModalClientUser?.role === 'ADMIN'}
                    onSaved={() => {
                        setAccessModalUser(null);
                        onAccessSaved();
                    }}
                />
            )}
        </>
    );
}

export default UserTable;
